import { useState } from "react";
import { useReminderDeleteMutation } from "web_myplann/Api/Reminders/useReminderDeleteMutation";
import { ReminderActionsDrawer } from "web_myplann/Reminders/Components/ReminderActionsDrawer";
import { ReminderCreateDrawer } from "web_myplann/Reminders/Components/ReminderCreateDrawer";
import { ReminderDeleteConfirmDrawer } from "web_myplann/Reminders/Components/ReminderDeleteConfirmDrawer";
import { ReminderEditDrawer } from "web_myplann/Reminders/Components/ReminderEditDrawer";
import { ReminderSnoozeDrawer } from "web_myplann/Reminders/Components/ReminderSnoozeDrawer";
import { RemindersCardList } from "web_myplann/Reminders/Components/RemindersCardList";
import { RemindersFlatList } from "web_myplann/Reminders/Components/RemindersFlatList";
import { useScrollToReminderRow } from "web_myplann/Reminders/Hooks/useScrollToReminderRow";
import type {
	Reminder,
	ReminderEntity,
	RemindersSectionVariant,
} from "web_myplann/Reminders/Types/ReminderTypes";

type ActiveDrawer =
	| { type: "none" }
	| { type: "create" }
	| { type: "edit"; reminder: Reminder }
	| { type: "actions"; reminder: Reminder }
	| { type: "snooze"; reminder: Reminder }
	| { type: "deleteConfirm"; reminder: Reminder };

type Props = {
	entity: ReminderEntity;
	reminders: Reminder[] | undefined;
	isLoading: boolean;
	isError: boolean;
	variant?: RemindersSectionVariant;
};

export const RemindersSectionView = ({
	entity,
	reminders = [],
	isLoading,
	isError,
	variant = "card",
}: Props) => {
	const [active, setActive] = useState<ActiveDrawer>({ type: "none" });

	const highlightedId = useScrollToReminderRow(!isLoading && !isError);

	const close = () => setActive({ type: "none" });

	const { mutate: deleteReminder } = useReminderDeleteMutation();

	const remove = (reminder: Reminder) => {
		close();
		deleteReminder(reminder.id);
	};

	if (isError) {
		return null;
	}

	return (
		<>
			{variant === "flat" ? (
				<RemindersFlatList
					reminders={reminders}
					isLoading={isLoading}
					highlightedId={highlightedId}
					onAdd={() => setActive({ type: "create" })}
					onTap={(reminder) => setActive({ type: "actions", reminder })}
				/>
			) : (
				<RemindersCardList
					reminders={reminders}
					isLoading={isLoading}
					highlightedId={highlightedId}
					onAdd={() => setActive({ type: "create" })}
					onTap={(reminder) => setActive({ type: "actions", reminder })}
					onRemove={(reminder) =>
						setActive({ type: "deleteConfirm", reminder })
					}
				/>
			)}

			{active.type === "create" && (
				<ReminderCreateDrawer
					entity={entity}
					existingReminders={reminders}
					open
					onClose={close}
					onOpenExisting={(reminder) => setActive({ type: "edit", reminder })}
				/>
			)}

			{active.type === "edit" && (
				<ReminderEditDrawer
					entity={entity}
					reminder={active.reminder}
					open
					onClose={close}
				/>
			)}

			{active.type === "actions" && (
				<ReminderActionsDrawer
					reminder={active.reminder}
					open
					onClose={close}
					onEdit={() => setActive({ type: "edit", reminder: active.reminder })}
					onSnooze={() =>
						setActive({ type: "snooze", reminder: active.reminder })
					}
					onDelete={() =>
						setActive({ type: "deleteConfirm", reminder: active.reminder })
					}
				/>
			)}

			{active.type === "snooze" && (
				<ReminderSnoozeDrawer reminder={active.reminder} open onClose={close} />
			)}

			{active.type === "deleteConfirm" && (
				<ReminderDeleteConfirmDrawer
					reminder={active.reminder}
					open
					onConfirm={() => remove(active.reminder)}
					onClose={close}
				/>
			)}
		</>
	);
};
